<template>
  <widget-dropdown
    ref="dropdownRef"
    :readonly="readonly"
    @update:model-value="$emit('update:dropdown', $event)"
    @show="showHandler"
    @hide="hideHandler"
  >
    <slot name="selected-item" />

    <template v-slot:menu="props">
      <slot
        name="before-options"
        v-bind="props"
      />

      <template v-if="preparedOptions.length">
        <widget-dropdown-item
          v-for="option in preparedOptions"
          :key="option.value"
          @click="$emit('update:model-value', option.value)"
        >
          <slot
            name="option"
            :option="option"
          >
            <widget-icon
              v-if="option.icon"
              :name="option.icon"
              class="distinct--text"
              size="14px"
              left
            />
            <span>{{ option.text }}</span>
          </slot>
        </widget-dropdown-item>
      </template>

      <q-item v-else>
        <q-item-section class="text-grey">
          {{ $t('common.noResults') }}
        </q-item-section>
      </q-item>

      <div ref="bottomElement" />
    </template>
  </widget-dropdown>
</template>

<script lang="ts">
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import WidgetDropdown from '@widgets/shared/components/dropdown/WidgetDropdown.vue'
import WidgetDropdownItem from '@widgets/shared/components/dropdown/WidgetDropdownItem.vue'
import type { PropType } from '@/composition/vue/compositionApi'
import { computed, defineComponent, onBeforeUnmount, ref, watch } from '@/composition/vue/compositionApi'
import type { SelectOption } from '@/services/forms/types'

export default defineComponent({
  components: {
    WidgetIcon,
    WidgetDropdown,
    WidgetDropdownItem,
  },
  props: {
    options: {
      type: Array as PropType<SelectOption[]>,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  emits: {
    'update:model-value': (_val: string) => true,
    'update:dropdown': (_val: boolean) => true,
    hide: (_e: MouseEvent) => true,
  },
  setup(props, context) {
    const dropdownRef = ref<InstanceType<typeof WidgetDropdown>>()
    const bottomElement = ref<HTMLElement>()

    const gap = 20

    const quantityShowedElements = ref(gap)

    const preparedOptions = computed(() => {
      if (quantityShowedElements.value >= props.options.length) {
        return props.options
      }

      return props.options.slice(0, quantityShowedElements.value)
    })

    const intersectHandler = () => {
      quantityShowedElements.value += gap
    }

    let observer: IntersectionObserver | null = null

    onBeforeUnmount(() => {
      observer?.disconnect()
    })

    const showHandler = () => {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry.isIntersecting) {
            return
          }
          intersectHandler()
        },
        {
          root: dropdownRef.value!.getMenuWrapperElement(),
          rootMargin: '0px 0px 100px 0px',
        },
      )
      observer.observe(bottomElement.value!)
    }

    const hideHandler = (e) => {
      context.emit('hide', e)
      observer?.disconnect()
      quantityShowedElements.value = gap
    }

    watch(
      () => props.options.length,
      () => {
        quantityShowedElements.value = gap
      },
    )

    return {
      dropdownRef,
      bottomElement,
      preparedOptions,
      showHandler,
      hideHandler,
    }
  },
})
</script>
